import { Component, Input } from '@angular/core';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { Router } from '@angular/router';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { DokulinkItemBuilderFactory } from '../../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { keysToTranslate } from '../../../../i18n/keys-to-translate';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GrundstuecknummerPipe } from '../../../shared/pipes/simple/grundstuecknummer.pipe';
import { KompaktGrundstueckPipe } from '../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.pipe';
import { KompaktGrundstueckbeschreibungPipe } from '../../../shared/kompaktdarstellung/grundstueck-beschreibung/kompakt-grundstueckbeschreibung.pipe';
import { GrundstueckIcpListEntry } from '../../../core/gb-api';
import { TranslateNamePipe } from '../../../shared/pipes/simple/translate-name.pipe';
import { ArrayAbbreviatePipe } from '../../../shared/pipes/simple/array-abbreviate.pipe';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { DecimalHochkommaPipe } from '../../../shared/pipes/simple/decimalHochkomma.pipe';
import { MapResponse } from '../../../core/maps-api';
import { KompaktGebaeudeeingangsadressePipe } from '../../../shared/kompaktdarstellung/gebaeudeeingangsadresse/kompakt-gebaeudeeingangsadresse-pipe';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { HistoryDirective } from '../../../shared/directives/history.directive';
import { LinkComponent } from '../../../shared/components/link/link.component';
import { FremdsystemeComponent } from '../../../shared/components/fremdsysteme/fremdsysteme.component';
import { AuszugIcpComponent } from '../../../shared/components/auszug-icp/auszug-icp.component';
import { GrundstueckFilterPipe } from '../../../shared/pipes/simple/grundstueck-filter.pipe';

@Component({
  selector: 'cap-meine-grundstuecke-icp-table',
  templateUrl: './meine-grundstuecke-icp-table.component.html',
  styleUrls: ['./meine-grundstuecke-icp-table.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    HistoryDirective,
    LinkComponent,
    FremdsystemeComponent,
    AuszugIcpComponent,
    TranslatePipe,
    GrundstueckFilterPipe
  ]
})
export class MeineGrundstueckeIcpTableComponent extends AbstractComponent {
  @Input()
  filterTerm: string = '';

  @Input()
  showZeilenSelect = true;

  tableSortModel = new TableSortModel<GrundstueckIcpListEntryVm>('table', this.comparatorService);
  _listLoader: ListLoader<GrundstueckIcpListEntryVm>;
  mapsLinkConfigured: boolean = false;

  constructor(
    protected router: Router,
    public appformatConfigService: FormatConfigService,
    private comparatorService: ComparatorService,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory
  ) {
    super();
    keysToTranslate<string>(['KeineDatenVorhanden', 'meineGrundstuecke.titel', 'PlanZuGrundstueckAnzeigen', 'Kurzbeschreibung']);
    this.tableSortModel
      .addColumn('nummer', (a, b) => this.comparatorService.grundstueckComparator(a.model, b.model), 'NUMMER')
      .switchSortDirection();
    this.tableSortModel
      .addColumn('egrid', (a, b) => this.comparatorService.grundstueckComparator(a.model, b.model), 'EGRID')
      .switchSortDirection();
    this.tableSortModel.addColumn('art', (a, b) => this.comparatorService.translatedStringComparator(a.kompaktArt, b.kompaktArt), 'ART');
    this.addSub(
      this.appformatConfigService.settingsConfig$.subscribe((config) => {
        this.mapsLinkConfigured = config?.isMapsLinkConfigured;
      })
    );
  }

  @Input()
  set listLoader(value: ListLoader<GrundstueckIcpListEntryVm>) {
    if (!value) {
      return;
    }
    this._listLoader = value;
    this.listLoader.applySortModel(this.tableSortModel);
  }

  get listLoader(): ListLoader<GrundstueckIcpListEntryVm> {
    return this._listLoader;
  }
}

export interface ViewModelCtx {
  translate: TranslateService;
  translateNamePipe: TranslateNamePipe;
  grundstuecknummerPipe: GrundstuecknummerPipe;
  kompaktGrundstueckPipe: KompaktGrundstueckPipe;
  kompaktGrundstueckBeschreibungPipe: KompaktGrundstueckbeschreibungPipe;
  kompaktGebaeudeeingangsadressePipe: KompaktGebaeudeeingangsadressePipe;
  abbreviatePipe: ArrayAbbreviatePipe;
  decimalHochkommaPipe: DecimalHochkommaPipe;
  dokuLinkBuilderFactory: DokulinkItemBuilderFactory;
}

export class GrundstueckIcpListEntryVm {
  mapLinkItem: MapResponse;

  constructor(
    public model: GrundstueckIcpListEntry,
    private ctx: ViewModelCtx
  ) {
    this.mapLinkItem = <MapResponse>{ url: null };
  }

  get kompaktGrundstueck(): string {
    return this.ctx.kompaktGrundstueckPipe.transform(this.model);
  }

  get tooltip(): string {
    return this.ctx.grundstuecknummerPipe.transform(this.model?.grundstueckNummer);
  }

  get egrid(): string {
    return this.model.egrid.value;
  }

  get kompaktKurzbeschreibung() {
    let entry = this.model;
    const flurname = this.ctx.abbreviatePipe.transform(entry.flurnamen, '...', 3, (val) => {
      return val;
    });
    const flaeche = entry.flaecheQuadratMeter ? this.ctx.decimalHochkommaPipe.transform(entry.flaecheQuadratMeter) + ' m²' : '';
    const sonderrecht = entry.sonderrechtText || '';
    return [flurname, flaeche, sonderrecht].filter((val) => val).join(', ');
  }

  get kompaktArt() {
    if (this.model.grundstueckArt === 'SDR' && this.model.sdrArt) {
      return this.ctx.translate.instant(this.model.grundstueckArt) + ' (' + this.ctx.translate.instant(this.model.sdrArt) + ')';
    }
    if (this.model.ligArt) {
      return this.ctx.translate.instant(this.model.ligArt + '_lang');
    }
    return this.ctx.translate.instant(this.model.grundstueckArt);
  }

  get kompaktAdressen(): string {
    const entries = this.model.gebaeudeAdressen;
    let adressen: string[] = [];
    for (let entry of entries) {
      adressen.push(this.ctx.kompaktGebaeudeeingangsadressePipe.transform(entry));
    }
    return this.ctx.abbreviatePipe.transform(adressen, '...', 3, (val) => {
      return val;
    });
  }
}
